import { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useToast
} from '@chakra-ui/react'
import {
  MdAccessTime,
  MdClose,
  MdDelete,
  MdEdit,
  MdEventRepeat,
  MdOutlinePerson,
  MdSave,
  MdVisibility
} from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { ModalContentComponent } from '../Modal/modalContent'
import { apiAuth } from '../../services/apiAuth'
import { queryClient } from '../../services/reactQuery'
import { Input } from '../Form/Input'
import { BlocksData } from '../../hooks/scheduling/useScheduling'
import { PermissionComponent } from '../Permissions'
import {
  formatHour,
  formatDateTime,
  toDateTimeInput,
  toUtcFromClinic
} from '../../utils/timezone'
import { useAuth } from '../../hooks/auth'

const BASE_URL = 'clinics/block-times/'

const WEEK_DAY_OPTIONS = [
  { value: '1', label: 'Segunda' },
  { value: '2', label: 'Terça' },
  { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' },
  { value: '5', label: 'Sexta' },
  { value: '6', label: 'Sábado' },
  { value: '7', label: 'Domingo' }
]

interface SpecialistOption {
  id: number
  name: string
}

interface BlockTimesModalProps {
  isOpen: boolean
  onClose: () => void
  block: BlocksData | null
  specialistOptions: SpecialistOption[]
}

type BlockFormState = {
  name: string
  description: string
  isRecurrent: boolean
  weekDays: string[]
  startTime: string
  finishTime: string
  recurrentStartTime: string
  recurrentFinishTime: string
  selectedSpecialists: string[]
}

const emptyState: BlockFormState = {
  name: '',
  description: '',
  isRecurrent: false,
  weekDays: [],
  startTime: '',
  finishTime: '',
  recurrentStartTime: '',
  recurrentFinishTime: '',
  selectedSpecialists: []
}

const splitDateTime = (value: string) => {
  const normalized = value.trim().replace(' ', 'T')
  const [date, hour] = normalized.split('T')
  return {
    date,
    hour
  }
}

const toTimeInput = (value?: string | null) => {
  if (!value) return ''
  return value.substring(0, 5)
}

const parseWeekDays = (weekDay?: string | null) => {
  if (!weekDay) return []

  return weekDay
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

const readableWeekDays = (weekDay?: string | null) => {
  const selected = parseWeekDays(weekDay)

  if (selected.length === 0) return '—'

  return WEEK_DAY_OPTIONS.filter((item) => selected.includes(item.value))
    .map((item) => item.label)
    .join(', ')
}

const buildInitialState = (
  block: BlocksData | null,
  timezone: string
): BlockFormState => {
  if (!block) return emptyState

  return {
    name: block.name || '',
    description: block.description || '',
    isRecurrent: Boolean(block.is_recurrent),
    weekDays: parseWeekDays(block.week_day),
    startTime: toDateTimeInput(block.start_time || '', timezone),
    finishTime: toDateTimeInput(block.finish_time || '', timezone),
    recurrentStartTime: toTimeInput(block.recurrent_start_time),
    recurrentFinishTime: toTimeInput(block.recurrent_finish_time),
    selectedSpecialists:
      block.specialists?.map((specialist) => String(specialist.id)) || []
  }
}

const buildPayload = (state: BlockFormState, timezone: string) => {
  const payload: Record<string, any> = {
    name: state.name,
    description: state.description || null,
    is_recurrent: state.isRecurrent,
    specialists: state.selectedSpecialists.map((value) => ({
      id: Number(value)
    }))
  }

  if (state.isRecurrent) {
    payload.week_day =
      state.weekDays.length > 0 ? state.weekDays.join(',') : null

    if (state.recurrentStartTime) {
      payload.recurrent_start_time = `${state.recurrentStartTime}:00`
    }

    if (state.recurrentFinishTime) {
      payload.recurrent_finish_time = `${state.recurrentFinishTime}:00`
    }

    payload.start_time = null
    payload.finish_time = null
  } else {
    payload.week_day = null
    payload.recurrent_start_time = null
    payload.recurrent_finish_time = null

    if (state.startTime) {
      const { date, hour } = splitDateTime(state.startTime)

      if (date && hour) {
        payload.start_time = toUtcFromClinic(date, hour, timezone)
      }
    }

    if (state.finishTime) {
      const { date, hour } = splitDateTime(state.finishTime)

      if (date && hour) {
        payload.finish_time = toUtcFromClinic(date, hour, timezone)
      }
    }
  }

  return payload
}

export function BlockTimesModal({
  isOpen,
  onClose,
  block,
  specialistOptions
}: BlockTimesModalProps) {
  const toast = useToast()
  const [t] = useTranslation('pageScheduling')
  const { decodeToken } = useAuth()

  const timezone = decodeToken?.clinic?.timezone || 'UTC'

  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState<BlockFormState>(emptyState)
  const [currentBlock, setCurrentBlock] = useState<BlocksData | null>(null)

  useEffect(() => {
    setCurrentBlock(block)
    setForm(buildInitialState(block, timezone))
    setIsEditing(false)
  }, [block, isOpen, timezone])

  const resetForm = () => {
    setForm(buildInitialState(currentBlock, timezone))
    setIsEditing(false)
  }

  const handleChange = <K extends keyof BlockFormState>(
    key: K,
    value: BlockFormState[K]
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const validateForm = () => {
    if (!form.name.trim()) {
      toast({
        title: 'O nome do bloqueio é obrigatório',
        status: 'warning',
        duration: 3000
      })
      return false
    }

    if (form.selectedSpecialists.length === 0) {
      toast({
        title: 'Selecione ao menos um especialista',
        status: 'warning',
        duration: 3000
      })
      return false
    }

    if (form.isRecurrent) {
      if (form.weekDays.length === 0) {
        toast({
          title: 'Selecione ao menos um dia da semana',
          status: 'warning',
          duration: 3000
        })
        return false
      }

      if (!form.recurrentStartTime || !form.recurrentFinishTime) {
        toast({
          title: 'Informe os horários da recorrência',
          status: 'warning',
          duration: 3000
        })
        return false
      }

      if (form.recurrentStartTime >= form.recurrentFinishTime) {
        toast({
          title: 'O horário final deve ser maior que o inicial',
          status: 'warning',
          duration: 3000
        })
        return false
      }
    } else {
      if (!form.startTime || !form.finishTime) {
        toast({
          title: 'Informe início e fim do bloqueio',
          status: 'warning',
          duration: 3000
        })
        return false
      }

      if (form.startTime >= form.finishTime) {
        toast({
          title: 'A data/hora final deve ser maior que a inicial',
          status: 'warning',
          duration: 3000
        })
        return false
      }
    }

    return true
  }

  const handleSave = async () => {
    if (!currentBlock) return
    if (!validateForm()) return

    setLoading(true)

    try {
      const payload = buildPayload(form, timezone)

      // debug
      console.log('payload', payload)

      const { data } = await apiAuth.patch(
        `${BASE_URL}${currentBlock.id}/`,
        payload
      )

      setCurrentBlock(data)
      setForm(buildInitialState(data, timezone))

      toast({
        title: 'Bloqueio atualizado com sucesso',
        status: 'success',
        duration: 3000
      })

      queryClient.invalidateQueries('scheduling')
      setIsEditing(false)
    } catch (error) {
      toast({
        title: t('errors.update_failed') || 'Erro ao atualizar bloqueio',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!currentBlock) return

    setLoading(true)

    try {
      await apiAuth.delete(`${BASE_URL}${currentBlock.id}/`)

      toast({
        title: 'Bloqueio excluído com sucesso',
        status: 'success',
        duration: 3000
      })

      queryClient.invalidateQueries('scheduling')
      onClose()
    } catch (error) {
      toast({
        title: t('errors.delete_failed') || 'Erro ao excluir bloqueio',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLoading(false)
    }
  }

  if (!currentBlock) return null

  return (
    <ModalContentComponent
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Editar bloqueio' : 'Visualizar bloqueio'}
      notRenderFooter
    >
      <Flex flexDir="column" gap={5}>
        <Flex
          justify="space-between"
          align={{ base: 'start', md: 'center' }}
          flexDir={{ base: 'column', md: 'row' }}
          gap={3}
        >
          <HStack spacing={3} align="center">
            <Flex
              w="40px"
              h="40px"
              rounded="full"
              bg="red.50"
              align="center"
              justify="center"
              color="red.500"
            >
              <Icon as={isEditing ? MdEdit : MdVisibility} boxSize={5} />
            </Flex>

            <Box>
              <Text fontWeight="bold" fontSize="lg">
                {currentBlock.name || 'Bloqueio'}
              </Text>
              <HStack mt={1} spacing={2} wrap="wrap">
                <Badge
                  colorScheme={currentBlock.is_recurrent ? 'purple' : 'blue'}
                >
                  {currentBlock.is_recurrent ? 'Recorrente' : 'Período único'}
                </Badge>

                {!!currentBlock.description && (
                  <Badge colorScheme="gray">Com descrição</Badge>
                )}
              </HStack>
            </Box>
          </HStack>

          <PermissionComponent ipCode="EDIT" spiCode="CALENDAR_BLOCKING">
            <HStack spacing={2}>
              {!isEditing ? (
                <>
                  <Tooltip label="Editar">
                    <IconButton
                      aria-label="edit-block"
                      size="sm"
                      icon={<MdEdit />}
                      onClick={() => setIsEditing(true)}
                    />
                  </Tooltip>

                  <Tooltip label="Excluir">
                    <IconButton
                      aria-label="delete-block"
                      size="sm"
                      colorScheme="red"
                      icon={<MdDelete />}
                      isLoading={loading}
                      onClick={handleDelete}
                    />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip label="Salvar">
                    <IconButton
                      aria-label="save-block"
                      size="sm"
                      icon={<MdSave />}
                      colorScheme="green"
                      isLoading={loading}
                      onClick={handleSave}
                    />
                  </Tooltip>

                  <Tooltip label="Cancelar">
                    <IconButton
                      aria-label="cancel-edit-block"
                      size="sm"
                      icon={<MdClose />}
                      onClick={resetForm}
                    />
                  </Tooltip>
                </>
              )}
            </HStack>
          </PermissionComponent>
        </Flex>

        <Divider />

        {!isEditing ? (
          <Stack spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Nome
                </Text>
                <Text fontWeight="semibold">{currentBlock.name || '—'}</Text>
              </Box>

              <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
                <Text fontSize="sm" color="gray.500" mb={1}>
                  Tipo
                </Text>
                <HStack>
                  <Icon
                    as={
                      currentBlock.is_recurrent ? MdEventRepeat : MdAccessTime
                    }
                    color={
                      currentBlock.is_recurrent ? 'purple.500' : 'blue.500'
                    }
                  />
                  <Text fontWeight="semibold">
                    {currentBlock.is_recurrent ? 'Recorrente' : 'Período único'}
                  </Text>
                </HStack>
              </Box>
            </SimpleGrid>

            <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
              <Text fontSize="sm" color="gray.500" mb={1}>
                Descrição
              </Text>
              <Text whiteSpace="pre-wrap">
                {currentBlock.description || '—'}
              </Text>
            </Box>

            {currentBlock.is_recurrent ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box p={4} bg="purple.50" rounded="md" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Dias da semana
                  </Text>
                  <Text fontWeight="semibold">
                    {readableWeekDays(currentBlock.week_day)}
                  </Text>
                </Box>

                <Box p={4} bg="purple.50" rounded="md" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Horário recorrente
                  </Text>
                  <Text fontWeight="semibold">
                    {currentBlock?.recurrent_start_time
                      ? currentBlock?.recurrent_start_time?.slice(0, 5)
                      : '--'}{' '}
                    até{' '}
                    {currentBlock?.recurrent_finish_time
                      ? currentBlock?.recurrent_finish_time?.slice(0, 5)
                      : '--'}
                  </Text>
                </Box>
              </SimpleGrid>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box p={4} bg="blue.50" rounded="md" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Início
                  </Text>
                  <Text fontWeight="semibold">
                    {currentBlock.start_time
                      ? formatDateTime(currentBlock?.start_time, timezone)
                      : '—'}
                  </Text>
                </Box>

                <Box p={4} bg="blue.50" rounded="md" borderWidth="1px">
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Fim
                  </Text>
                  <Text fontWeight="semibold">
                    {currentBlock?.finish_time
                      ? formatDateTime(currentBlock?.finish_time, timezone)
                      : '—'}
                  </Text>
                </Box>
              </SimpleGrid>
            )}

            <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
              <Text fontSize="sm" color="gray.500" mb={3}>
                Especialistas
              </Text>

              {currentBlock?.specialists?.length > 0 ? (
                <Flex wrap="wrap" gap={2}>
                  {currentBlock?.specialists?.map((specialist) => (
                    <Badge
                      key={specialist.id}
                      px={3}
                      py={1}
                      rounded="full"
                      colorScheme="teal"
                    >
                      {specialist.social_name || specialist.name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                <Text>—</Text>
              )}
            </Box>

            <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
              <Text fontSize="sm" color="gray.500" mb={1}>
                Criado em
              </Text>
              <Text fontWeight="semibold">
                {currentBlock?.created_at
                  ? formatDateTime(currentBlock?.created_at, timezone)
                  : '—'}
              </Text>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Nome</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Nome do bloqueio"
                />
              </FormControl>

              {currentBlock?.is_recurrent ? (
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">É recorrente?</FormLabel>
                  <Checkbox
                    isChecked={form.isRecurrent}
                    onChange={(e) =>
                      handleChange('isRecurrent', e.target.checked)
                    }
                    isDisabled
                  >
                    Sim
                  </Checkbox>
                </FormControl>
              ) : null}
            </SimpleGrid>

            <FormControl>
              <FormLabel>Descrição</FormLabel>
              <Textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descrição"
              />
            </FormControl>

            {form.isRecurrent ? (
              <Box p={4} bg="purple.50" rounded="md" borderWidth="1px">
                <Stack spacing={4}>
                  <Text fontWeight="bold">Configurações da recorrência</Text>

                  <FormControl>
                    <FormLabel>Dias da semana</FormLabel>
                    <CheckboxGroup
                      value={form.weekDays}
                      onChange={(value) =>
                        handleChange('weekDays', value as string[])
                      }
                    >
                      <Flex wrap="wrap" gap={3}>
                        {WEEK_DAY_OPTIONS.map((item) => (
                          <Checkbox key={item.value} value={item.value}>
                            {item.label}
                          </Checkbox>
                        ))}
                      </Flex>
                    </CheckboxGroup>
                  </FormControl>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Início recorrente</FormLabel>
                      <Input
                        name="recurrent_start_time"
                        type="time"
                        value={form.recurrentStartTime}
                        onChange={(e) =>
                          handleChange('recurrentStartTime', e.target.value)
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Fim recorrente</FormLabel>
                      <Input
                        name="recurrent_finish_time"
                        type="time"
                        value={form.recurrentFinishTime}
                        onChange={(e) =>
                          handleChange('recurrentFinishTime', e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>
                </Stack>
              </Box>
            ) : (
              <Box p={4} bg="blue.50" rounded="md" borderWidth="1px">
                <Stack spacing={4}>
                  <Text fontWeight="bold">Período único</Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Início</FormLabel>
                      <Input
                        name="start_time"
                        type="datetime-local"
                        value={form.startTime}
                        onChange={(e) =>
                          handleChange('startTime', e.target.value)
                        }
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Fim</FormLabel>
                      <Input
                        name="finish_time"
                        type="datetime-local"
                        value={form.finishTime}
                        onChange={(e) =>
                          handleChange('finishTime', e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>
                </Stack>
              </Box>
            )}

            <Box p={4} bg="gray.50" rounded="md" borderWidth="1px">
              <FormControl>
                <FormLabel>Especialistas</FormLabel>
                <CheckboxGroup
                  value={form.selectedSpecialists}
                  onChange={(value) =>
                    handleChange('selectedSpecialists', value as string[])
                  }
                >
                  <Flex wrap="wrap" gap={3}>
                    {specialistOptions.map((specialist) => (
                      <Checkbox
                        key={specialist.id}
                        value={String(specialist.id)}
                      >
                        <HStack spacing={1}>
                          <Icon as={MdOutlinePerson} />
                          <Text>{specialist.name}</Text>
                        </HStack>
                      </Checkbox>
                    ))}
                  </Flex>
                </CheckboxGroup>
              </FormControl>
            </Box>

            <Flex justify="flex-end" gap={3}>
              <Button variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSave}
                isLoading={loading}
                leftIcon={<MdSave />}
              >
                Salvar alterações
              </Button>
            </Flex>
          </Stack>
        )}
      </Flex>
    </ModalContentComponent>
  )
}
